import { format } from "date-fns"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import DueDateField from "src/main/Billing/DueDateField"
import PaymentMethodDropdown from "src/main/Billing/PaymentMethodDropdown"
import { useUpdateInvoice } from "src/main/Billing/Payments/hooks"
import RefundPaymentMethodDropdown, {
  refundMethodOptions,
} from "src/main/Billing/RefundPaymentMethodDropdown"
import { parseDateValue } from "src/main/Billing/helpers"
import { useGetRefundablePaymentMethods } from "src/main/Billing/hooks"
import InvoiceAutopayFields from "src/main/Service/Manage/WorkOrders/InvoiceAutopayFields"

import Button from "src/components/Button"
import Modal from "src/components/Modal"
import { ADD_NEW_PAYMENT_METHOD } from "src/components/SelectPaymentMethod/SelectPaymentMethod"

import { updateInvoiceAutopay } from "src/api/Service/WorkOrders"

import { useToast } from "src/hooks/use_toast"

import { useInvoiceTracker } from "./useInvoiceTracker"

const Footer = ({ onClose, onSave, isSaving, isSaveDisabled }) => (
  <Modal.Footer>
    <div className="flex justify-end gap-2">
      <Button variant="tertiary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={onSave}
        isLoading={isSaving}
        disabled={isSaveDisabled}
      >
        Save
      </Button>
    </div>
  </Modal.Footer>
)

Footer.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  isSaveDisabled: PropTypes.bool,
}

// A work order invoice's payment method is its AutoPay card, saved through the work order as its page does.
const WorkOrderDetails = ({ context, onClose, onSaved, onError }) => {
  const { marinaSlug } = useMarina()
  const track = useInvoiceTracker()
  const { container, invoice } = context
  const seeded = Boolean(invoice.payment_method)
  const [enabled, setEnabled] = useState(seeded)
  const [cardId, setCardId] = useState(
    seeded ? invoice.payment_method.stripePaymentMethodId ?? "" : ""
  )

  const { mutate, isLoading } = useMutation(
    () =>
      updateInvoiceAutopay(
        marinaSlug,
        container.work_order_sequential_id,
        invoice.id,
        { autopayEnabled: enabled, stripePaymentMethodId: cardId }
      ),
    {
      onSuccess: () => {
        track("details_saved", { autopay_enabled: enabled })
        onSaved()
      },
      onError,
    }
  )

  return (
    <>
      <Modal.Body>
        <InvoiceAutopayFields
          workOrderSequentialId={container.work_order_sequential_id}
          contactId={container.contact_id}
          enabled={enabled}
          onEnabledChange={setEnabled}
          stripePaymentMethodId={cardId}
          onPaymentMethodChange={setCardId}
          woDefaultEnabled={Boolean(container.work_order_autopay_enabled)}
        />
      </Modal.Body>
      <Footer
        onClose={onClose}
        onSave={() => mutate()}
        isSaving={isLoading}
        isSaveDisabled={
          enabled && (!cardId || cardId === ADD_NEW_PAYMENT_METHOD)
        }
      />
    </>
  )
}

WorkOrderDetails.propTypes = {
  context: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
}

// Reservations, contracts and sales save through the invoice, as PaymentModal's Details section does.
const InvoiceDetails = ({ context, onClose, onSaved, onError }) => {
  const { invoice } = context
  const originalPaymentMethodId = invoice.payment_method?.id ?? "manual"
  const originalDueDate = parseDateValue(invoice.due_date)
  const isRefund = invoice.total_amount < 0

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      invoice: {
        payment_method_id: originalPaymentMethodId,
        due_date: originalDueDate,
      },
    },
  })
  const paymentMethodId = watch("invoice.payment_method_id")
  const dueDate = watch("invoice.due_date")
  const isPaymentMethodChanged =
    String(paymentMethodId) !== String(originalPaymentMethodId)
  const isDueDateChanged = originalDueDate?.getTime() !== dueDate?.getTime()

  const { data: refundMethods, isFetching: isLoadingRefundMethods } =
    useGetRefundablePaymentMethods({
      billingContainerId: invoice.billing_container_id,
      options: {
        select: (data) =>
          refundMethodOptions(data, {
            currentPaymentMethod: invoice.payment_method,
            selectedId: originalPaymentMethodId,
            balanceCents: Math.abs(invoice.balance),
          }),
        enabled: isRefund,
      },
    })

  const track = useInvoiceTracker()
  const { submit, isLoading } = useUpdateInvoice({
    paymentId: invoice.id,
    onSuccess: () => {
      track("details_saved", {
        payment_method_changed: isPaymentMethodChanged,
        due_date_changed: isDueDateChanged,
      })
      onSaved()
    },
    onError,
  })

  const onSave = handleSubmit((data) => {
    const changes = {}
    if (isPaymentMethodChanged) {
      changes.payment_method_id = data.invoice.payment_method_id
    }
    if (isDueDateChanged) {
      changes.due_date = format(data.invoice.due_date, "yyyy-MM-dd")
    }
    submit({ invoice: changes })
  })

  return (
    <>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          {isRefund ? (
            <RefundPaymentMethodDropdown
              id="edit-invoice-refund-method"
              isLoading={isLoadingRefundMethods}
              paymentMethods={refundMethods}
              name="invoice.payment_method_id"
              control={control}
              existingPaymentMethod={invoice.payment_method}
            />
          ) : (
            <PaymentMethodDropdown
              name="invoice.payment_method_id"
              onlineMethods={context.payment_methods.cards}
              control={control}
              includeExpirationDates
              existingPaymentMethod={invoice.payment_method}
            />
          )}
          <DueDateField
            name="invoice.due_date"
            disabled={!context.edit_invoice_due_dates}
            control={control}
            minDate={parseDateValue(context.marina_today)}
            error={errors?.invoice?.due_date}
            rules={{ required: "Due date is required" }}
          />
        </div>
      </Modal.Body>
      <Footer
        onClose={onClose}
        onSave={onSave}
        isSaving={isLoading}
        isSaveDisabled={!isPaymentMethodChanged && !isDueDateChanged}
      />
    </>
  )
}

InvoiceDetails.propTypes = {
  context: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
}

const EditInvoiceDetailsModal = ({ context, onClose, onSaved }) => {
  const showToast = useToast()
  const saved = () => {
    showToast("Invoice updated", { type: "success" })
    onSaved()
  }
  const failed = (error) =>
    showToast(error?.message || "Couldn't update the invoice.", {
      type: "error",
    })
  const Details =
    context.container.kind === "work_order" ? WorkOrderDetails : InvoiceDetails

  return (
    <Modal isOpen onClose={onClose} size="default">
      <Modal.Header>
        <h1 className="m-0 text-lg font-semibold">Edit details</h1>
      </Modal.Header>
      <Details
        context={context}
        onClose={onClose}
        onSaved={saved}
        onError={failed}
      />
    </Modal>
  )
}

EditInvoiceDetailsModal.propTypes = {
  context: PropTypes.shape({
    container: PropTypes.shape({ kind: PropTypes.string.isRequired })
      .isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
}

export default EditInvoiceDetailsModal
